import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { capabilities } from '../mock';
import useReveal from '../hooks/useReveal';

const Capabilities = () => {
  const ref = useReveal({ threshold: 0.1 });

  return (
    <section id="capabilities" ref={ref} className="relative py-16 md:py-20 [@media(max-height:500px)]:py-16 px-6 md:px-10 border-t border-white/5 overflow-hidden">
      <div className="pointer-events-none absolute -left-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--green)]/[0.07] blur-3xl" />

      <div className="relative max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-[.55fr_.45fr] gap-10 items-end mb-16 [@media(max-height:500px)]:mb-8">
          <div>
            <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
              &mdash; What We Do
            </div>
            <h2 className="reveal font-display text-[38px] md:text-[64px] [@media(max-height:500px)]:text-[34px] leading-[1.02] text-[color:var(--parchment)]">
              Four steps of<br/>
              a <span className="text-[color:var(--green)]">full-service</span> estate sale.
            </h2>
          </div>
          <p className="reveal text-[color:var(--parchment)]/70 leading-relaxed max-w-md md:justify-self-end [@media(max-height:500px)]:text-[14px]">
            From consultation and preparation to staging, marketing, and on-site sale &mdash; our proven process is designed to maximize value while minimizing stress for every client.
          </p>
        </div>

        {/* Craftsmanship banner */}
        <figure className="reveal relative mb-16 md:mb-20 rounded-2xl overflow-hidden border border-white/10">
          <div className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9] [@media(max-height:500px)]:aspect-auto [@media(max-height:500px)]:h-[220px]">
            <img
              src="/what-we-do.webp" srcSet="/what-we-do-400w.webp 400w, /what-we-do-800w.webp 800w, /what-we-do.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
              alt="A bronze French bulldog and a small succulent resting on a live-edge wood coffee table in a warm, low-lit room"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Artifact Aperture treatment */}
            <div className="absolute inset-0 bg-[color:var(--obsidian)]/40" />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--obsidian)] via-[color:var(--obsidian)]/20 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-l from-[color:var(--obsidian)]/70 via-transparent to-transparent" />

            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-10 [@media(max-height:500px)]:p-4">
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div className="max-w-xl md:text-right md:ml-auto [@media(max-height:500px)]:text-left [@media(max-height:500px)]:ml-0">
                  <p className="font-display text-[15px] sm:text-[18px] md:text-[26px] [@media(max-height:500px)]:text-[15px] leading-[1.2] text-[color:var(--parchment)]">
                    From heirloom bronzes to live-edge tables &mdash; <span className="text-[color:var(--green)]">every piece is handled with the care it deserves</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </figure>

        <div className="grid md:grid-cols-4 [@media(max-height:500px)]:grid-cols-2 gap-0 border-t border-white/10">
          {capabilities.map((c, i) => (
            <article
              key={c.number}
              className={`reveal pillar-col group relative py-10 px-4 md:px-8 [@media(max-height:500px)]:py-6 [@media(max-height:500px)]:px-4 hover:bg-[color:var(--obsidian-2)]/60 transition-colors duration-500 ${i === 0 ? 'md:pl-0' : ''}`}
              style={{ transitionDelay: `${i * 90}ms` }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/45">{c.number}</div>
                <ArrowUpRight className="w-4 h-4 text-[color:var(--parchment)]/40 group-hover:text-[color:var(--green)] transition-colors" />
              </div>
              <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-2">{c.kicker}</div>
              <h3 className="font-display text-[26px] md:text-[30px] [@media(max-height:500px)]:text-[20px] leading-tight text-[color:var(--parchment)]">
                {c.title}
              </h3>
              <p className="mt-4 text-[color:var(--parchment)]/65 text-[14.5px] [@media(max-height:500px)]:text-[13px] leading-relaxed">{c.body}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {c.tags.map((t) => (
                  <span key={t} className="font-mono text-[12px] md:text-[10px] tracking-[0.18em] uppercase px-2.5 py-1 rounded-full border border-white/10 text-[color:var(--parchment)]/70">
                    {t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Capabilities;
